import {
  ListAppliedFilterChips,
  ListFiltersDrawer,
  ListTablePaginationBar,
  type ListAppliedFilterChipItem,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OperatorDetailCustomersTable } from "./operator-detail-customers-table.component";
import { OperatorDetailCustomersToolbar } from "./operator-detail-customers-toolbar.component";
import type { OperatorDetail, OperatorDetailCustomerRow } from "../types";

const CUSTOMERS_PAGE_SIZE = 50;

const DEMO_COMPANIES = [
  "Walmart",
  "Target",
  "Costco Wholesale",
  "Home Depot",
  "Amazon Logistics",
  "FedEx Freight",
  "UPS Supply Chain",
  "DHL Express",
  "Sysco",
  "PepsiCo",
  "Kroger",
  "Albertsons",
] as const;

const DEMO_LOCATIONS = [
  "Joilet",
  "Houston",
  "Dallas",
  "Chicago",
  "Indianapolis",
  "Phoenix",
  "Seattle",
  "Atlanta",
  "Miami",
  "Denver",
  "Portland",
  "Boston",
] as const;

/** Demo customers for operator detail Customers tab (aviro-only); replace with API when wired. */
const OPERATOR_DETAIL_CUSTOMERS_BY_ID: Record<string, OperatorDetailCustomerRow[]> = {
  "operator-row-1": buildDemoCustomerRows(243),
};

const DEFAULT_CUSTOMERS = OPERATOR_DETAIL_CUSTOMERS_BY_ID["operator-row-1"] ?? [];

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
] as const;

export interface OperatorDetailCustomersProps {
  operator: OperatorDetail;
}

export function OperatorDetailCustomers({ operator }: OperatorDetailCustomersProps) {
  const [searchValue, setSearchValue] = useState("");
  const [sortDescending, setSortDescending] = useState(true);
  const [listPage, setListPage] = useState(1);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<Record<string, string[]>>({});
  const [appliedLocationFilters, setAppliedLocationFilters] = useState<string[]>([]);
  const [appliedStatusFilters, setAppliedStatusFilters] = useState<string[]>([]);

  const sourceRows = useMemo(() => {
    return OPERATOR_DETAIL_CUSTOMERS_BY_ID[operator.id] ?? DEFAULT_CUSTOMERS;
  }, [operator.id]);

  const locationFilterOptions = useMemo(
    () =>
      Array.from(new Set(sourceRows.map((row) => row.locationLabel))).map((label) => ({
        value: label,
        label,
      })),
    [sourceRows],
  );

  const listFilterSections = useMemo(
    () => [
      { id: "status", label: "Status", options: [...STATUS_FILTER_OPTIONS] },
      { id: "location", label: "Location", options: locationFilterOptions },
    ],
    [locationFilterOptions],
  );

  const appliedChipItems: ListAppliedFilterChipItem[] = useMemo(() => {
    const items: ListAppliedFilterChipItem[] = [];
    for (const v of appliedStatusFilters) {
      const option = STATUS_FILTER_OPTIONS.find((opt) => opt.value === v);
      items.push({
        id: `status:${v}`,
        label: option?.label ?? v,
        onRemove: () => {
          setAppliedStatusFilters((prev) => prev.filter((val) => val !== v));
          setListPage(1);
        },
      });
    }
    for (const v of appliedLocationFilters) {
      items.push({
        id: `location:${v}`,
        label: v,
        onRemove: () => {
          setAppliedLocationFilters((prev) => prev.filter((val) => val !== v));
          setListPage(1);
        },
      });
    }
    return items;
  }, [appliedLocationFilters, appliedStatusFilters]);

  const handleClearAllFilterChips = useCallback(() => {
    setAppliedLocationFilters([]);
    setAppliedStatusFilters([]);
    setListPage(1);
  }, []);

  const filteredRows = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    const rows = sourceRows.filter((row) => {
      if (showOnlyActive && row.status !== "active") {
        return false;
      }
      if (appliedStatusFilters.length > 0 && !appliedStatusFilters.includes(row.status)) {
        return false;
      }
      if (appliedLocationFilters.length > 0 && !appliedLocationFilters.includes(row.locationLabel)) {
        return false;
      }
      if (q.length === 0) return true;
      const haystack = [row.companyName, row.locationLabel, row.primaryContact, row.email, row.status]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const result = a.companyName.localeCompare(b.companyName, undefined, { sensitivity: "base" });
      return sortDescending ? result * -1 : result;
    });
    return sorted;
  }, [appliedLocationFilters, appliedStatusFilters, searchValue, showOnlyActive, sortDescending, sourceRows]);

  const handleSearchTrigger = useCallback((value: string) => {
    setSearchValue(value);
    setListPage(1);
  }, []);

  const handleCompanySortToggle = useCallback(() => {
    setSortDescending((current) => !current);
  }, []);

  const handleOpenFilterDrawer = useCallback(() => {
    setDraftFilters({
      status: [...appliedStatusFilters],
      location: [...appliedLocationFilters],
    });
    setFilterDrawerOpen(true);
  }, [appliedLocationFilters, appliedStatusFilters]);

  const handleApplyFilters = useCallback((nextFilters: Record<string, string[]>) => {
    setAppliedStatusFilters(nextFilters.status ?? []);
    setAppliedLocationFilters(nextFilters.location ?? []);
    setListPage(1);
    setFilterDrawerOpen(false);
  }, []);

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / CUSTOMERS_PAGE_SIZE));
    if (listPage > pageCount) {
      setListPage(pageCount);
    }
  }, [filteredRows.length, listPage]);

  useEffect(() => {
    setAppliedLocationFilters([]);
    setAppliedStatusFilters([]);
    setFilterDrawerOpen(false);
    setDraftFilters({});
  }, [operator.id]);

  const pagedRows = useMemo(() => {
    const fromIndex = (listPage - 1) * CUSTOMERS_PAGE_SIZE;
    return filteredRows.slice(fromIndex, fromIndex + CUSTOMERS_PAGE_SIZE);
  }, [filteredRows, listPage]);

  return (
    <div className="operator-detail-customers">
      <OperatorDetailCustomersToolbar
        searchValue={searchValue}
        onSearchTrigger={handleSearchTrigger}
        onFilterClick={handleOpenFilterDrawer}
        showOnlyActive={showOnlyActive}
        onShowOnlyActiveChange={(value) => {
          setShowOnlyActive(value);
          setListPage(1);
        }}
      />
      <ListAppliedFilterChips
        items={appliedChipItems}
        onClearAll={handleClearAllFilterChips}
        totalResults={filteredRows.length}
      />
      <div className="users-page__table-scroll">
        <OperatorDetailCustomersTable
          rows={pagedRows}
          sortDescending={sortDescending}
          onCompanyNameHeaderClick={handleCompanySortToggle}
          paginationFooter={
            <ListTablePaginationBar
              totalCount={filteredRows.length}
              page={listPage}
              pageSize={CUSTOMERS_PAGE_SIZE}
              onPageChange={setListPage}
            />
          }
        />
      </div>
      <ListFiltersDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filters"
        subtitle="Select filters for customers."
        sections={listFilterSections}
        value={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApplyFilters}
      />
    </div>
  );
}


function buildDemoCustomerRows(count: number): OperatorDetailCustomerRow[] {
  return Array.from({ length: count }, (_, index) => {
    const company = DEMO_COMPANIES[index % DEMO_COMPANIES.length];
    const location = DEMO_LOCATIONS[index % DEMO_LOCATIONS.length];
    const status = index % 7 === 0 ? "inactive" : "active";
    const slug = company.toLowerCase().replace(/[^a-z0-9]+/g, "");
    return {
      id: `op-detail-cust-${index + 1}`,
      /** One id per row so each company name navigates to a distinct customer detail URL. */
      customerId: `customer-row-${index + 1}`,
      companyName: company,
      locationLabel: location,
      primaryContact: `Contact ${index + 1}`,
      email: `user${index + 1}@${slug}.example.com`,
      status,
    };
  });
}